import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  getCurrentUser,
} from "../../services/auth";

import {
  getSmsBalance,
  getMembers,
  getKanda,
  getJumuia,
  sendBulkSms,
  getSmsJob
} from "../../services/api";

import {
  toast,
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import "./sms.css";

/* =========================================
   SMS COUNTER
========================================= */

function isGsm7(text = "") {
  const gsmChars =
    /^[A-Za-z0-9\s@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\~\]|€]*$/;

  return gsmChars.test(text);
}

function countSmsSegments(
  message = ""
) {
  const text = String(
    message || ""
  );

  const gsm =
    isGsm7(text);

  const length =
    text.length;

  // GSM
  if (gsm) {
    if (length <= 160) {
      return {
        encoding:
          "GSM-7",
        length,
        segments: 1,
      };
    }

    return {
      encoding:
        "GSM-7",
      length,
      segments:
        Math.ceil(
          length / 153
        ),
    };
  }

  // Unicode
  if (length <= 70) {
    return {
      encoding:
        "UNICODE",
      length,
      segments: 1,
    };
  }

  return {
    encoding:
      "UNICODE",
    length,
    segments:
      Math.ceil(
        length / 67
      ),
  };
}

/* =========================================
   CONSTANTS
========================================= */

const MEMBER_TYPES = [
  {
    value: "ALL",
    label:
      "All Members",
  },
  {
    value:
      "LEADERS_ONLY",
    label:
      "Only Leaders",
  },
];

const DEFAULT_SENDER_IDS =
  [
    "ZAKA-PAY",
    "SADAKA-PAY",
    "AHADI",
    "MAVUNO",
    "PAROKIA",
    "UWAKA",
    "WAWATA"
    // "INFORM"
  ];

/* =========================================
   COMPONENT
========================================= */

export default function BulkSMSPage() {
  const user =
    getCurrentUser();

  const [loading, setLoading] =
    useState(false);

  const [sending, setSending] =
    useState(false);

  const [smsBalance, setSmsBalance] =
    useState(0);

  const [members, setMembers] =
    useState([]);

  const [kandaList, setKandaList] =
    useState([]);

  const [jumuiaList, setJumuiaList] =
    useState([]);

  const [selectedKanda,
    setSelectedKanda] =
    useState("");

  const [selectedJumuia,
    setSelectedJumuia] =
    useState("");

  const [logs, setLogs] =
    useState([]);

  const [jobId, setJobId] =
    useState(null);

  const [jobStatus, setJobStatus] =
    useState(null);

  const [showFilters, setShowFilters] =
    useState(false);

  const [showCompose, setShowCompose] = 
    useState(true);

  const [currentPage, setCurrentPage] =
    useState(1);

const pageSize = 20;
  const [form, setForm] =
    useState({
      memberType: "ALL",
      senderId: "",
      message: "",
      dynamicSms: false,
      scheduleDate: "",
      search: "",
    });

  /* =========================================
     LOAD
  ========================================= */

  useEffect(() => {
    load();
  }, []);

  /* =========================================
   CLEANUP TIMER
========================================= */

useEffect(() => {

  return () => {

    if (window.smsJobTimer) {
      clearInterval(window.smsJobTimer);
    }

  };

}, []);

  const load = async () => {
    try {
      setLoading(true);

      const [
        balanceRes,
        membersRes,
        kandaRes,
      ] = await Promise.all([
        getSmsBalance(),
        getMembers(),
        getKanda(
          user.parokiaId
        ),
      ]);

      setSmsBalance(
        Number(
          balanceRes?.data
            ?.balance || 0
        )
      );

      setMembers(
        Array.isArray(
          membersRes?.data
        )
          ? membersRes.data
          : []
      );

      setKandaList(
        kandaRes?.data || []
      );
    } catch (err) {
      console.error(err);

      toast.error(
        "Failed to load page"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     LOAD JUMUIA
  ========================================= */

  useEffect(() => {
    if (
      !selectedKanda
    ) {
      setJumuiaList([]);
      return;
    }

    loadJumuia();
  }, [selectedKanda]);

  const loadJumuia =
    async () => {
      try {
        const res =
          await getJumuia(
            user.parokiaId,
            selectedKanda
          );

        setJumuiaList(
          res.data || []
        );
      } catch (err) {
        console.error(err);
      }
    };

  /* =========================================
      JOB STATUS
    ========================================= */

  const loadJobStatus =
    async (
      currentJobId
    ) => {

      try {

        const res =
          await getSmsJob(
            currentJobId
          );

        setJobStatus(
          res.data
        );

        const balanceRes = await getSmsBalance();

        setSmsBalance(
            Number(balanceRes.data.balance || 0)
        );

        if (
          res.data?.status ===
          "COMPLETED"
        ) {

          clearInterval(
            window.smsJobTimer
          );

          toast.success(
            toast.success(
              `Bulk SMS completed.
              Sent: ${res.data.success_count}
              Failed: ${res.data.failed_count}`
              )
          );

          load(); // refresh balance

          setTimeout(() => {
              setJobId(null);
              setJobStatus(null);
          }, 5000);

        }

      } catch (err) {

        console.error(
          err
        );
      }
    };

  /* =========================================
     UPDATE FORM
  ========================================= */

  const updateForm = (
    key,
    value
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /* =========================================
     FILTER MEMBERS
  ========================================= */

  const filteredMembers =
    useMemo(() => {
      let rows = [
        ...members,
      ];

      if (
        selectedKanda
      ) {
        rows = rows.filter(
          (m) =>
            String(
              m.kanda_id
            ) ===
            String(
              selectedKanda
            )
        );
      }

      if (
        selectedJumuia
      ) {
        rows = rows.filter(
          (m) =>
            String(
              m.jumuia_id
            ) ===
            String(
              selectedJumuia
            )
        );
      }

      if (
        form.memberType ===
        "LEADERS_ONLY"
      ) {
        rows = rows.filter(
          (m) =>
            Number(
              m.is_jumuia_leader
            ) === 1
        );
      }

      if (
        form.search
      ) {
        const q =
          form.search.toLowerCase();

        rows = rows.filter(
          (m) =>
            m.full_name
              ?.toLowerCase()
              .includes(q) ||
            m.phone?.includes(
              q
            ) ||
            m.member_code
              ?.toLowerCase()
              .includes(q)
        );
      }

      return rows;
    }, [
      members,
      selectedKanda,
      selectedJumuia,
      form.memberType,
      form.search,
    ]);

    const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredMembers.length /
        pageSize
      )
    );

  const pagedMembers =
    filteredMembers.slice(
      (currentPage - 1) *
        pageSize,
      currentPage *
        pageSize
    );

  /* =========================================
     RECIPIENTS
  ========================================= */

  const recipientPhones =
    useMemo(() => {
      return [
        ...new Set(
          filteredMembers
            .map(
              (m) =>
                m.phone
            )
            .filter(Boolean)
        ),
      ];
    }, [filteredMembers]);

  /* =========================================
     SMS ANALYSIS
  ========================================= */

  const smsInfo =
    useMemo(() => {
      return countSmsSegments(
        form.message
      );
    }, [form.message]);

  const totalEstimatedUsage =
    smsInfo.segments *
    recipientPhones.length;

  const insufficientBalance =
    totalEstimatedUsage >
    smsBalance;

  const progressPercent =
    jobStatus
      ? Math.round(
          (
            jobStatus.processed_count /
            Math.max(
              1,
              jobStatus.total_recipients
            )
          ) * 100
        )
      : 0;

  /* =========================================
     SEND
  ========================================= */

  const handleSend =
    async () => {
      if (
        !form.senderId
      ) {
        toast.error(
          "Select Sender ID"
        );

        return;
      }

      if (
        !form.message.trim()
      ) {
        toast.error(
          "Enter SMS message"
        );

        return;
      }

      if (
        recipientPhones.length ===
        0
      ) {
        toast.error(
          "No recipients selected"
        );

        return;
      }

      if (
        insufficientBalance
      ) {
        toast.error(
          "Insufficient SMS balance"
        );

        return;
      }

      try {
        setSending(true);

        const payload = {
          senderId:
            form.senderId,

          message:
            form.message,

          scheduleDate:
            form.scheduleDate ||
            null,

          memberIds:
            filteredMembers.map(
              (m) =>
                m.member_id
            ),

          dynamicSms: true,

          filters: {
            kandaId:
              selectedKanda ||
              null,

            jumuiaId:
              selectedJumuia ||
              null,

            memberType:
              form.memberType,
          },
        };

        const res =
          await sendBulkSms(
            payload
          );
        const currentJobId =
          res.data.jobId;

        setJobId(
          currentJobId
        );

        await loadJobStatus(currentJobId);

        if (window.smsJobTimer) {
            clearInterval(window.smsJobTimer);
        }

        toast.success(
          `Bulk SMS started. Job #${currentJobId}`
        );

        window.smsJobTimer =
          setInterval(() => {

            loadJobStatus(
              currentJobId
            );

          }, 3000);

        // setLogs(
        //   Array.isArray(
        //     res?.data
        //       ?.results
        //   )
        //     ? res.data
        //       .results
        //     : []
        // );

        // setSmsBalance(
        //   Number(
        //     res?.data
        //       ?.remainingBalance ||
        //     0
        //   )
        // );
        
        setLogs([]);
        //load();

        toast.success(
          //`SMS processed. Success: ${res?.data?.success}`
          "Bulk SMS queued successfully."
        );
      } catch (err) {
        console.error(
          err
        );

        toast.error(
          err?.response
            ?.data
            ?.message ||
          "Bulk SMS failed"
        );
      } finally {
        setSending(false);
      }
    };

  /* =========================================
     UI
  ========================================= */

  return (
    <div className="sms-page">
      {/* HEADER */}

      <div className="sms-card">

  <div className="sms-header-row">

    <div>
      <h2>Bulk SMS..</h2>

      <p>
        Send SMS to church members
      </p>
    </div>

    <div className="sms-dashboard">

      <div className="sms-stat-card">
        <span>Balance</span>
        <strong>
          {smsBalance.toLocaleString()}
        </strong>
      </div>

      <div className="sms-stat-card">
        <span>Recipients</span>
        <strong>
          {recipientPhones.length}
        </strong>
      </div>

      <div className="sms-stat-card">
        <span>Usage</span>
        <strong>
          {totalEstimatedUsage}
        </strong>
      </div>

      <div className="sms-stat-card">
        <span>Job Status</span>
        <strong>
          {jobStatus?.status || "IDLE"}
        </strong>
      </div>

    </div>

  </div>

</div>

          
      {/* FILTERS */}

      <div className="sms-card">
        <div className="sms-grid">
          <div>
            <label>
              Audience
            </label>

            <select
              className="sms-input"
              value={
                form.memberType
              }
              onChange={(
                e
              ) =>
                updateForm(
                  "memberType",
                  e.target
                    .value
                )
              }
            >
              {MEMBER_TYPES.map(
                (
                  item
                ) => (
                  <option
                    key={
                      item.value
                    }
                    value={
                      item.value
                    }
                  >
                    {
                      item.label
                    }
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label>
              Kanda
            </label>

            <select
              className="sms-input"
              value={
                selectedKanda
              }
              onChange={(
                e
              ) =>
                setSelectedKanda(
                  e.target
                    .value
                )
              }
            >
              <option value="">
                All Kanda
              </option>

              {kandaList.map(
                (k) => (
                  <option
                    key={
                      k.kanda_id
                    }
                    value={
                      k.kanda_id
                    }
                  >
                    {
                      k.kanda_name
                    }
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label>
              Jumuia
            </label>

            <select
              className="sms-input"
              value={
                selectedJumuia
              }
              onChange={(
                e
              ) =>
                setSelectedJumuia(
                  e.target
                    .value
                )
              }
            >
              <option value="">
                All Jumuia
              </option>

              {jumuiaList.map(
                (j) => (
                  <option
                    key={
                      j.jumuia_id
                    }
                    value={
                      j.jumuia_id
                    }
                  >
                    {
                      j.jumuia_name
                    }
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label>
              Search
            </label>

            <input
              className="sms-input"
              placeholder="Search member..."
              value={
                form.search
              }
              onChange={(
                e
              ) =>
                updateForm(
                  "search",
                  e.target
                    .value
                )
              }
            />
          </div>
        </div>
      </div>

      {/* COMPOSE */}

      <div className="sms-card">
        <div className="sms-grid">
          <div>
            <label>
              Sender ID
            </label>

            <select
              className="sms-input"
              value={
                form.senderId
              }
              onChange={(
                e
              ) =>
                updateForm(
                  "senderId",
                  e.target
                    .value
                )
              }
            >
              <option value="">
                Select Sender ID
              </option>

              {DEFAULT_SENDER_IDS.map(
                (
                  id
                ) => (
                  <option
                    key={
                      id
                    }
                    value={
                      id
                    }
                  >
                    {id}
                  </option>
                )
              )}
            </select>
          </div>

          <div>
            <label>
              Schedule
            </label>

            <input
              type="datetime-local"
              className="sms-input"
              value={
                form.scheduleDate
              }
              onChange={(
                e
              ) =>
                updateForm(
                  "scheduleDate",
                  e.target
                    .value
                )
              }
            />
          </div>
        </div>

        <div
          style={{
            marginTop: 20,
          }}
        >
          <label>
            Message
          </label>

          <textarea
            className="sms-textarea"
            placeholder="Example: Hello #NAME#, welcome to #PAROKIA#"
            value={
              form.message
            }
            onChange={(
              e
            ) =>
              updateForm(
                "message",
                e.target
                  .value
              )
            }
          />

          <div className="sms-helper">
            Variables:
            {" #NAME# "}
            {" #MEMBER_CODE# "}
            {" #PAROKIA# "}
            {" #JUMUIA# "}
          </div>
        </div>

        {/* SMS ANALYTICS */}

        <div className="sms-analysis">
          <div>
            Characters:
            <strong>
              {" "}
              {
                smsInfo.length
              }
            </strong>
          </div>

          {/* <div>
            Encoding:
            <strong>
              {" "}
              {
                smsInfo.encoding
              }
            </strong>
          </div> */}
          

          <div>
            SMS Units:
            <strong>
              {" "}
              {
                smsInfo.segments
              }
            </strong>
          </div>

          <div>
            Recipients:
            <strong>
              {" "}
              {
                recipientPhones.length
              }
            </strong>
          </div>

          <div>
            Estimated Usage:
            <strong>
              {" "}
              {
                totalEstimatedUsage
              }
            </strong>
          </div>
        </div>

        {smsInfo.segments >
          1 && (
            <div className="sms-warning">
              This message
              exceeds 160
              characters and
              will consume{" "}
              {
                smsInfo.segments
              }{" "}
              SMS units per
              recipient.
            </div>
          )}

        {insufficientBalance && (
          <div className="sms-error-box">
            Insufficient SMS
            balance.
            Required:{" "}
            {
              totalEstimatedUsage
            }
            , Available:{" "}
            {smsBalance}
          </div>
        )}

       <div className="sms-row no-border">
              <div />
          <button
            className="sms-action-btn"
            disabled={
              sending ||
              insufficientBalance ||
              jobStatus?.status === "PENDING" ||
              jobStatus?.status === "PROCESSING"
            }
            onClick={
              handleSend
            }
          >
            {
              sending
                  ? "Submitting..."
                  : jobStatus?.status === "PROCESSING"
                  ? "Job Running..."
                  : "Send Bulk SMS"
                                
              }
          </button>
        </div>
      </div>


      {jobStatus && (

  <div className="sms-card">

    <div className="sms-preview-title">
      SMS Job #{jobId}
    </div>

    <div
      className="job-progress-bar"
    >
      <div
        className="job-progress-fill"
        style={{
          width:
            `${progressPercent}%`
        }}
      />
    </div>

    <div
      style={{
        marginTop: 10
      }}
    >
      {progressPercent}% Complete
    </div>

    <div>
      Status:
      {" "}
      <strong>
        {
          jobStatus.status
        }
      </strong>
    </div>

    <div>
      Processed:
      {" "}
      {
        jobStatus.processed_count
      }
      /
      {
        jobStatus.total_recipients
      }
    </div>

    <div>
      Success:
      {" "}
      {
        jobStatus.success_count
      }
    </div>

    <div>
      Failed:
      {" "}
      {
        jobStatus.failed_count
      }
    </div>

  </div>

)}
      {/* PREVIEW */}

      <div className="sms-card">
        <div className="sms-preview-title">
          Recipient Preview
        </div>

        <div className="sms-table-wrap">
          <table className="sms-table">
            <thead>
              <tr>
                <th>#</th>
                <th>
                  Member Code
                </th>
                <th>
                  Name
                </th>
                <th>
                  Phone
                </th>
                <th>
                  Kanda
                </th>
                <th>
                  Jumuia
                </th>
              </tr>
            </thead>

            <tbody>
              {pagedMembers
                .slice(
                  0,
                  100
                )
                .map(
                  (
                    m,
                    i
                  ) => (
                    <tr
                      key={
                        m.member_id
                      }
                    >
                      <td>
                        {i +
                          1}
                      </td>

                      <td>
                        {
                          m.member_code
                        }
                      </td>

                      <td>
                        {
                          m.full_name
                        }
                      </td>

                      <td>
                        {
                          m.phone
                        }
                      </td>

                      <td>
                        {
                          m.kanda_name
                        }
                      </td>

                      <td>
                        {
                          m.jumuia_name
                        }
                      </td>
                    </tr>
                  )
                )}
            </tbody>
          </table>

          <div className="sms-pagination">

          <button
            disabled={
              currentPage === 1
            }
            onClick={() =>
              setCurrentPage(
                currentPage - 1
              )
            }
          >
            Previous
          </button>

          <span>
            Page {currentPage}
            {" of "}
            {totalPages}
          </span>

          <button
            disabled={
              currentPage ===
              totalPages
            }
            onClick={() =>
              setCurrentPage(
                currentPage + 1
              )
            }
          >
            Next
          </button>

        </div>
        </div>
      </div>

      {/* LOGS */}

      {logs.length >
        0 && (
          <div className="sms-card">
            <div className="sms-preview-title">
              SMS Results
            </div>

            <div className="sms-table-wrap">
              <table className="sms-table">
                <thead>
                  <tr>
                    <th>
                      Phone
                    </th>
                    <th>
                      Status
                    </th>
                    <th>
                      Reason
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {logs.map(
                    (
                      log,
                      i
                    ) => (
                      <tr
                        key={
                          i
                        }
                      >
                        <td>
                          {
                            log.phone
                          }
                        </td>

                        <td>
                          {
                            log.status
                          }
                        </td>

                        <td>
                          {log.reason ||
                            "-"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

      <ToastContainer />
    </div>
  );
}